import { useState, useEffect, useCallback } from "react";
import QMSLayout from "../components/QMSLayout";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import {
  Brain, Search, Calculator, BookOpen, Map, MessageCircle,
  Loader2, ChevronRight, Send, Zap, Beaker, Gauge, AlertTriangle
} from "lucide-react";

const AuditorsBrainPage = () => {
  const { t } = useTranslation('audit');
  const [activeTab, setActiveTab] = useState("explorer");
  const [domains, setDomains] = useState([]);
  const [topics, setTopics] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [stats, setStats] = useState(null);
  const [calculators, setCalculators] = useState({});
  const [calcType, setCalcType] = useState("cpk");
  const [calcInputs, setCalcInputs] = useState({});
  const [calcResult, setCalcResult] = useState(null);
  const [playbooks, setPlaybooks] = useState({});
  const [selectedPB, setSelectedPB] = useState(null);
  const [mentorQ, setMentorQ] = useState("");
  const [mentorAnswer, setMentorAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [dRes, sRes, cRes, pRes] = await Promise.all([
        fetch(`${API}/brain/domains`, { credentials: "include" }),
        fetch(`${API}/brain/stats`, { credentials: "include" }),
        fetch(`${API}/brain/calculators`, { credentials: "include" }),
        fetch(`${API}/brain/playbooks`, { credentials: "include" }),
      ]);
      if (dRes.ok) setDomains((await dRes.json()).domains || []);
      if (sRes.ok) setStats(await sRes.json());
      if (cRes.ok) setCalculators((await cRes.json()).calculators || {});
      if (pRes.ok) setPlaybooks((await pRes.json()).playbooks || {});
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const searchKB = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q: searchQ, limit: "30" });
      if (sectorFilter) params.set("sector", sectorFilter);
      const res = await fetch(`${API}/brain/search?${params}`, { credentials: "include" });
      if (res.ok) setTopics((await res.json()).results || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const seedKB = async () => {
    setSeeding(true);
    try {
      const res = await fetch(`${API}/brain/seed-knowledge`, { method: "POST", credentials: "include" });
      if (res.ok) { await fetchData(); searchKB(); }
    } catch (e) { console.error(e); }
    setSeeding(false);
  };

  const runCalc = async () => {
    try {
      const res = await fetch(`${API}/brain/calculate`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ calculator: calcType, inputs: calcInputs }),
      });
      if (res.ok) setCalcResult(await res.json());
    } catch (e) { console.error(e); }
  };

  const askMentor = async () => {
    if (!mentorQ.trim()) return;
    setLoading(true);
    setMentorAnswer(null);
    try {
      const res = await fetch(`${API}/brain/ask`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ question: mentorQ }),
      });
      if (res.ok) setMentorAnswer(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const tabs = [
    { id: "explorer", label: "Knowledge Explorer", icon: Search },
    { id: "calculators", label: "Formula Calculators", icon: Calculator },
    { id: "playbooks", label: "Sector Playbooks", icon: Map },
    { id: "mentor", label: "AI Mentor", icon: MessageCircle },
  ];

  const calcInputDefs = { cpk: ["usl", "lsl", "mean", "sigma"], aql: ["lot_size", "aql_percent"], weibull: ["beta"], arrhenius: ["ea_ev", "t_use_c", "t_test_c"], power: ["sigma", "delta"], mtbf: ["total_hours", "failures"], gage_rr: ["grr_percent"], rss: ["tolerances"] };

  return (
    <QMSLayout title={t('common:modules.audit')}>
      <div className="space-y-6" data-testid="auditors-brain-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>The Auditor's Brain</h1>
            <p className="text-slate-400 text-sm mt-1">Knowledge Explorer, Formula Calculators, Sector Playbooks, AI Mentor</p>
          </div>
          {stats && <div className="flex items-center gap-4 text-xs text-slate-400">
            <span>{stats.total_nodes} knowledge nodes</span><span>{stats.domain_count} domains</span>
          </div>}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#0A1628] p-1 rounded-[3px] border border-[#1E3A5F]">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-[3px] text-xs font-bold uppercase tracking-wide transition-all ${activeTab === t.id ? "bg-[#1A6BCC] text-white shadow-[0_0_15px_rgba(0,200,224,0.2)]" : "text-slate-400 hover:text-white"}`}>
              <t.icon className="w-4 h-4" />{t.label}
            </button>
          ))}
        </div>

        {/* Knowledge Explorer */}
        {activeTab === "explorer" && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-2 bg-[#132340] rounded-[3px] px-4 py-2.5 border border-[#1E3A5F]">
                <Search className="w-4 h-4 text-slate-400" />
                <input type="text" placeholder="Search: Cpk, Weibull, AQL, Gage R&R..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchKB()} className="bg-transparent text-white text-sm outline-none flex-1" data-testid="kb-search" />
              </div>
              <select value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)} className="bg-[#132340] border border-[#1E3A5F] text-white text-xs p-2.5 rounded-[3px] outline-none" data-testid="sector-filter">
                <option value="">All Sectors</option>
                <option value="medical">Medical</option><option value="aerospace">Aerospace</option><option value="automotive">Automotive</option><option value="electronics">Electronics</option><option value="lab">Lab/Metrology</option>
              </select>
              <button onClick={searchKB} className="px-4 py-2.5 bg-[#1A6BCC] text-white text-xs font-bold rounded-[3px]" data-testid="search-btn">{t('common:actions.search')}</button>
              <button onClick={seedKB} disabled={seeding} className="px-4 py-2.5 bg-emerald-600 text-white text-xs font-bold rounded-[3px] disabled:opacity-50" data-testid="seed-kb-btn">
                {seeding ? t('auditorsBrain.seeding') : t('auditorsBrain.seedKb')}
              </button>
            </div>

            {domains.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {domains.map((d) => (
                  <button key={d.domain} onClick={() => { setSearchQ(d.domain); searchKB(); }} className="px-3 py-1.5 bg-[#132340] border border-[#1E3A5F] text-slate-300 text-xs rounded-[3px] hover:border-[#00C8E0] transition-colors">
                    {d.domain} ({d.count})
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-2">
              {topics.map((t, i) => (
                <div key={i} className="p-4 bg-[#132340] border border-[#1E3A5F] rounded-[3px]">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-white font-semibold text-sm">{t.topic}</h4>
                    <span className="text-xs text-slate-500 bg-[#0F1E35] px-2 py-0.5 rounded">{t.domain}</span>
                  </div>
                  {t.formula && <div className="text-[#00C8E0] font-mono text-xs bg-[#0A1628] p-2 rounded-[3px] mb-2">{t.formula}</div>}
                  {t.threshold && <div className="text-xs text-slate-400 mb-1"><strong>Threshold:</strong> {t.threshold}</div>}
                  <div className="text-xs text-yellow-400/90 bg-yellow-500/5 p-2 rounded-[3px] border-l-2 border-yellow-500/40 mt-2">{t.auditor_tip}</div>
                  {t.sector && <div className="flex gap-1 mt-2">{t.sector.map((s) => <span key={s} className="text-xs bg-[#0F1E35] text-slate-500 px-2 py-0.5 rounded">{s}</span>)}</div>}
                </div>
              ))}
              {topics.length === 0 && !loading && <p className="text-slate-500 text-sm text-center py-8">Search the knowledge base or click "Seed KB" to populate with engineering domains.</p>}
            </div>
          </div>
        )}

        {/* Calculators */}
        {activeTab === "calculators" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="space-y-2">
              {Object.entries(calculators).map(([k, v]) => (
                <button key={k} onClick={() => { setCalcType(k); setCalcInputs({}); setCalcResult(null); }} data-testid={`calc-${k}`}
                  className={`w-full text-left p-3 rounded-[3px] border transition-all text-xs ${calcType === k ? "bg-[#1A6BCC]/20 border-[#1A6BCC] text-white" : "bg-[#132340] border-[#1E3A5F] text-slate-300 hover:border-[#00C8E0]/40"}`}>
                  <div className="font-bold">{v.name}</div>
                  <div className="text-slate-500 mt-0.5">{v.standard}</div>
                </button>
              ))}
            </div>
            <div className="lg:col-span-2 bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6">
              <h3 className="text-white font-bold text-sm mb-1">{calculators[calcType]?.name}</h3>
              <p className="text-[#00C8E0] font-mono text-xs mb-4">{calculators[calcType]?.formula}</p>
              <div className="grid grid-cols-2 gap-3 mb-4">
                {(calcInputDefs[calcType] || []).map((inp) => (
                  <div key={inp}>
                    <label className="text-slate-400 text-xs block mb-1">{inp.replace(/_/g, " ").toUpperCase()}</label>
                    <input type="text" value={calcInputs[inp] || ""} onChange={(e) => setCalcInputs({ ...calcInputs, [inp]: inp === "tolerances" ? e.target.value : e.target.value })} placeholder={inp === "tolerances" ? "0.05, 0.03, 0.02" : ""} className="w-full bg-[#0F1E35] border border-[#1E3A5F] text-white text-xs p-2 rounded-[3px] focus:border-[#00C8E0] outline-none" data-testid={`calc-input-${inp}`} />
                  </div>
                ))}
              </div>
              <button onClick={() => {
                const inputs = { ...calcInputs };
                if (calcType === "rss" && typeof inputs.tolerances === "string") inputs.tolerances = inputs.tolerances.split(",").map(Number);
                setCalcInputs(inputs);
                runCalc();
              }} className="px-6 py-2 bg-[#1A6BCC] text-white text-xs font-bold uppercase rounded-[3px]" data-testid="run-calc-btn">Calculate</button>
              {calcResult && (
                <div className="mt-4 p-4 bg-[#0F1E35] border border-[#00C8E0]/20 rounded-[3px] space-y-2 text-xs">
                  {Object.entries(calcResult).filter(([k]) => !["calculator", "standard", "auditor_check"].includes(k)).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="text-slate-400">{k.replace(/_/g, " ")}</span><span className="text-white font-medium">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span></div>
                  ))}
                  {calcResult.auditor_check && <div className="mt-2 p-2 bg-yellow-500/5 border-l-2 border-yellow-500/40 text-yellow-400/90 text-xs">{calcResult.auditor_check}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sector Playbooks */}
        {activeTab === "playbooks" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(playbooks).map(([key, pb]) => (
              <div key={key} className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-5 hover:border-[#00C8E0]/40 transition-colors cursor-pointer" onClick={() => setSelectedPB(selectedPB === key ? null : key)} data-testid={`playbook-${key}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-bold text-sm">{pb.name}</h4>
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${selectedPB === key ? "rotate-90" : ""}`} />
                </div>
                <div className="flex flex-wrap gap-1 mb-3">{pb.standards?.map((s) => <span key={s} className="text-xs bg-[#0F1E35] text-[#00C8E0] px-2 py-0.5 rounded">{s}</span>)}</div>
                {selectedPB === key && (
                  <div className="space-y-3 mt-3 border-t border-[#1E3A5F] pt-3">
                    <div><h5 className="text-slate-400 text-xs font-bold mb-1">AUDIT CHECKLIST</h5>
                      <ul className="space-y-1">{pb.audit_checklist?.map((c, i) => <li key={i} className="text-xs text-slate-300 flex items-start gap-1.5"><ChevronRight className="w-3 h-3 mt-0.5 text-[#00C8E0]" />{c}</li>)}</ul></div>
                    <div><h5 className="text-slate-400 text-xs font-bold mb-1">REQUIRED FORMULAS</h5>
                      <div className="flex flex-wrap gap-1">{pb.required_formulas?.map((f) => <span key={f} className="text-xs bg-[#0A1628] text-[#00C8E0] px-2 py-0.5 rounded font-mono">{f}</span>)}</div></div>
                    <div><h5 className="text-red-400 text-xs font-bold mb-1">COMMON FINDINGS</h5>
                      <ul className="space-y-1">{pb.common_findings?.map((f, i) => <li key={i} className="text-xs text-red-400/80 flex items-start gap-1.5"><AlertTriangle className="w-3 h-3 mt-0.5" />{f}</li>)}</ul></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* AI Mentor */}
        {activeTab === "mentor" && (
          <div className="bg-[#132340] border border-[#1E3A5F] rounded-[3px] p-6">
            <div className="flex items-center gap-3 mb-4"><Brain className="w-5 h-5 text-[#00C8E0]" /><h3 className="text-white font-semibold text-sm uppercase tracking-wide" style={{ fontFamily: "'Syne', sans-serif" }}>Lead Auditor AI Mentor</h3></div>
            <p className="text-slate-400 text-xs mb-4">Ask anything about auditing, statistics, standards compliance, or specific formulas. The mentor draws from 1,000+ supplier audit experiences.</p>
            <div className="flex gap-2 mb-4 flex-wrap">
              {["When should I use Cpk vs Ppk?", "What sample size for validation?", "How to justify zero-acceptance sampling?", "Explain measurement uncertainty"].map((q) => (
                <button key={q} onClick={() => setMentorQ(q)} className="text-xs bg-[#0F1E35] border border-[#1E3A5F] text-slate-300 px-3 py-1.5 rounded-[3px] hover:border-[#00C8E0]/40">{q}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <input type="text" value={mentorQ} onChange={(e) => setMentorQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && askMentor()} placeholder="Ask the lead auditor..." className="flex-1 bg-[#0F1E35] border border-[#1E3A5F] text-white text-sm p-3 rounded-[3px] focus:border-[#00C8E0] outline-none" data-testid="mentor-input" />
              <button onClick={askMentor} disabled={loading} className="px-6 py-3 bg-[#1A6BCC] text-white text-xs font-bold uppercase rounded-[3px] disabled:opacity-50" data-testid="ask-mentor-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            {mentorAnswer && (
              <div className="mt-4 p-4 bg-[#0F1E35] border border-[#00C8E0]/20 rounded-[3px]">
                <div className="text-white text-sm whitespace-pre-wrap leading-relaxed">{mentorAnswer.answer}</div>
                <div className="mt-2 text-xs text-slate-500">KB sources referenced: {mentorAnswer.kb_sources}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </QMSLayout>
  );
};

export default AuditorsBrainPage;
